import { useMemo, useState } from 'react';
import { Text } from '@chakra-ui/react';
import type { TaxonomyRoleOut } from '@/client';
import ServiceCombobox, { type ServiceOptionGroup } from '@/components/services/ServiceCombobox';
import CreateRoleDialog from '@/components/services/role/CreateRoleDialog';
import { useContactTaxonomy } from '@/hooks/service-providers/useContactTaxonomy';
import { humaniseTrade } from '@/components/services/model/providerDirectory';
import { orderRoleGroups } from '@/components/services/role/roleGroupOrder';

/** Not offered, though a contact already filed under one still shows it. */
const HIDDEN_ROLE_CODES = new Set(['tenant']);

interface RolePickerProps {
	/** The role the contact is filed under now, by code. */
	value: string | null | undefined;
	/** The whole role, so a create can send its `code` and an edit its `id` without a second lookup. */
	onChange: (role: TaxonomyRoleOut) => void;
	houseId: number;
	canEdit: boolean;
	/**
	 * A role the property does not offer, kept in the list so the current value is always on show.
	 *
	 * The documents can read a trade this house has never filed anything under; dropping it would
	 * leave the box blank beside a contact that plainly has one, and take the answer away from a
	 * reader who only wanted to change something else.
	 */
	unlistedValue?: string | null;
	/** What the box says while nothing is chosen. */
	placeholder?: string;
}

/**
 * The role a contact is filed under, picked from this property's own catalog.
 *
 * One picker for every surface that asks — the contact form, the review pass, the composer — so a
 * role is offered, named and grouped the same way wherever it is chosen.
 *
 * Groups are headings rather than options: the group follows the role, and nothing on a contact
 * asks for one directly.
 *
 * Text that names no role can be created as a custom one, which is then chosen, so it reaches
 * `onChange` like a pick. A custom role cannot be renamed or deleted yet.
 */
const RolePicker = (props: RolePickerProps) => {
	const { value, onChange, houseId, canEdit, unlistedValue, placeholder = 'Search services' } = props;
	const { data: taxonomy } = useContactTaxonomy(houseId);
	/** The name a create was started from; null while no create is open. */
	const [createDraft, setCreateDraft] = useState<string | null>(null);

	const current = value?.trim() ?? '';

	const groups = useMemo<ServiceOptionGroup[]>(() => {
		const known = orderRoleGroups(taxonomy ?? [])
			.map((category) => ({
				label: category.label,
				options: (category.roles ?? [])
					.filter((role) => !HIDDEN_ROLE_CODES.has(role.code) || role.code === current)
					.map((role) => ({ value: role.code, label: role.label })),
			}))
			.filter((group) => group.options.length > 0);
		const unlisted = unlistedValue?.trim();
		const isKnown = (taxonomy ?? []).some((category) =>
			(category.roles ?? []).some((role) => role.code === unlisted),
		);
		return unlisted && !isKnown
			? [{ label: '', options: [{ value: unlisted, label: humaniseTrade(unlisted) }] }, ...known]
			: known;
	}, [taxonomy, unlistedValue, current]);

	const roleByCode = useMemo(() => {
		const byCode = new Map<string, TaxonomyRoleOut>();
		for (const category of taxonomy ?? []) for (const role of category.roles ?? []) byCode.set(role.code, role);
		return byCode;
	}, [taxonomy]);

	if (!canEdit) {
		const label = current ? (roleByCode.get(current)?.label ?? humaniseTrade(current)) : '';
		return (
			<Text py={3} px={4} fontSize="md" lineHeight="1.5" color={label ? undefined : 'fg.subtle'}>
				{label || 'No service'}
			</Text>
		);
	}

	return (
		<>
			<ServiceCombobox
				groups={groups}
				value={current}
				placeholder={placeholder}
				onPick={(code) => {
					const role = roleByCode.get(code);
					if (role) onChange(role);
				}}
				onCreate={setCreateDraft}
			/>
			<CreateRoleDialog
				houseId={houseId}
				groups={taxonomy ?? []}
				draft={createDraft}
				onDone={(role) => {
					setCreateDraft(null);
					if (role) onChange(role);
				}}
			/>
		</>
	);
};

export default RolePicker;
